import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import AvatarEditor from 'react-avatar-editor';
import styled from 'styled-components';

import {
  Box as MuiBox,
  Button as MuiButton,
  Dialog as MuiDialog,
  DialogContent as MuiDialogContent,
  Slider as MuiSlider,
} from '@mui/material';

import DialogTitle from 'components/Dialogs/DialogTitle';
import { useTranslation } from 'react-i18next';

const CropEditor = ({
  open,
  onClose,
  file,
  setFile,
  size = 300,
  square = false,
}) => {
  const { t } = useTranslation();

  const [image, setImage] = useState(file);
  const [sliderValue, setSliderValue] = useState(0);
  const [zoom, setZoom] = useState(1);
  const minZoom = 1;
  const maxZoom = 2;
  const editor = useRef(null);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(file);
  };

  const onSave = async () => {
    if (editor.current) {
      const canvasScaled = editor.current.getImageScaledToCanvas().toDataURL();
      const res = await fetch(canvasScaled);
      const blob = await res.blob();
      const newFile = new File([blob], image.name || 'title', {
        type: image.type || 'image/png',
      });

      setFile(
        Object.assign(newFile, { preview: URL.createObjectURL(newFile) })
      );
      onClose();
    }
  };

  const SliderChange = (e, value) => {
    setSliderValue(value);
    const newZoom = (maxZoom - minZoom) * (value / 100) + minZoom;
    setZoom(newZoom);
  };

  const handleWheel = (e) => {
    const newValue = sliderValue + (e.deltaY > 0 ? -10 : 10);
    // value between 0 and 100 cannot be exceeded

    if (newValue < 0) {
      SliderChange(e, 0);
    } else if (newValue > 100) {
      SliderChange(e, 100);
    } else {
      SliderChange(e, newValue);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    accept: ['.jpeg', '.jpg', '.png'],
    // Add more filetypes according to usecase

    multiple: false,
    autoFocus: true,
    noKeyboard: true,
  });

  return (
    <MuiDialog onClose={onClose} open={open}>
      <DialogTitle onClose={onClose}>{t('Crop Image')}</DialogTitle>
      <MuiDialogContent>
        <MuiBox width="100%" display="flex" justifyContent="center">
          {image ? (
            <div>
              <AvatarEditor
                ref={editor}
                image={image}
                width={size}
                height={size}
                border={50}
                borderRadius={square ? 0 : 360}
                color={[255, 255, 255, 0.6]} // RGBA color of the border
                scale={zoom}
                onWheel={handleWheel}
              />
              <SMuiSlider
                value={sliderValue}
                valueLabelDisplay="off"
                onChange={SliderChange}
              />
            </div>
          ) : (
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <CropZone square={square}>{t('Drop Image Here')}</CropZone>
            </div>
          )}
        </MuiBox>
        <MuiBox mt={3} display="flex" justifyContent="center" width="100%">
          <MuiButton
            data-id={'avatar-upload-cropper-submit-button'}
            disabled={!image}
            onClick={onSave}
            variant="outlined"
            size="large"
            color="primary"
          >
            {t('Crop')}
          </MuiButton>
        </MuiBox>
      </MuiDialogContent>
    </MuiDialog>
  );
};

export default CropEditor;

const SMuiSlider = styled(MuiSlider)`
  && {
    margin-top: 20px;
  }
`;

const CropZone = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 150px;
  height: 150px;
  border-radius: ${({ square }) => (square ? 0 : '50%')};
  border: 1px solid ${({theme}) => theme.palette.colors.gray400};
  color: ${({theme}) => theme.palette.colors.black100};
  margin: 20px;
  cursor: pointer;
`;
